import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddLegalSettings.css";
import DocumentTabs from "../TenantDocument/DocumentTabs";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/tenant-legal-settings/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const ndaRequirements = [
  { value: "NOT_REQUIRED", label: "Not Required" },
  { value: "TENANT_STANDARD", label: "Tenant Standard NDA" },
  { value: "PROJECT_SPECIFIC", label: "Project-Specific" },
  { value: "MUTUAL_NDA", label: "Mutual NDA" }
];

const classifications = [
  { value: "PUBLIC", label: "Public" },
  { value: "INTERNAL", label: "Internal" },
  { value: "CONFIDENTIAL", label: "Confidential" },
  { value: "RESTRICTED", label: "Restricted" }
];

const emptyLegalSettings = {
  id: null,
  nda_requirement: "NOT_REQUIRED",
  default_classification: "INTERNAL",
  retention_policy: ""
};

const AddLegalSettings = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ ...emptyLegalSettings });
  const [savedData, setSavedData] = useState({ ...emptyLegalSettings });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Fetch existing legal settings ----
  useEffect(() => {
    const fetchExisting = async () => {
      const tenantUser = getTenantUser();

      if (!tenantUser?.tenant_id) {
        setLoadingExisting(false);
        return;
      }

      try {
        setLoadingExisting(true);

        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantUser.tenant_id,
            tables: ["tenant_legal_settings"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;

        let rows = [];
        if (result?.data?.tenant_legal_settings) {
          rows = result.data.tenant_legal_settings;
        } else if (result?.tenant_legal_settings) {
          rows = result.tenant_legal_settings;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }

        const matched = rows.length ? rows[0] : null;

        if (matched) {
          const loaded = {
            id: matched.id,
            nda_requirement: matched.nda_requirement || "NOT_REQUIRED",
            default_classification:
              matched.default_classification || "INTERNAL",
            retention_policy: matched.retention_policy || ""
          };

          setFormData(loaded);
          setSavedData(loaded);
        } else {
          setFormData({ ...emptyLegalSettings });
          setSavedData({ ...emptyLegalSettings });
        }
      } catch (error) {
        console.error("Error fetching tenant legal settings:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Legal Settings",
          text: "Could not fetch tenant legal settings."
        });
        setFormData({ ...emptyLegalSettings });
        setSavedData({ ...emptyLegalSettings });
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const buildPayload = (tenantUser) => ({
    nda_requirement: formData.nda_requirement,
    default_classification: formData.default_classification,
    retention_policy: (formData.retention_policy || "").trim(),
    tenant: tenantUser.tenant_id
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData({ ...savedData });
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) return;

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID is missing from tenant_user."
      });
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload(tenantUser);

      if (formData.id) {
        await axios.put(`${API_URL}${formData.id}/`, payload, {
          headers: { "Content-Type": "application/json" }
        });
      } else {
        const response = await axios.post(API_URL, payload, {
          headers: { "Content-Type": "application/json" }
        });

        const newId =
          response.data?.id ||
          response.data?.data?.id ||
          response.data?.pk;

        if (newId) {
          setFormData((prev) => ({ ...prev, id: newId }));
          setSavedData((prev) => ({ ...prev, id: newId }));
        }
      }

      const refreshed = { ...formData };
      setSavedData(refreshed);
      setIsEditing(false);

      await Swal.fire({
        icon: "success",
        title: "Saved Successfully",
        confirmButtonText: "OK"
      });
    } catch (error) {
      console.error("Save Legal Settings Error:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: `Unable to save legal settings. ${extractError(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="legal-settings-page">
          <DocumentTabs />

          <div className="legal-settings-page-header">
            <div>
              <h2>Legal Settings</h2>
            </div>
          </div>

          {loadingExisting ? (
            <div className="legal-settings-loading">
              Loading legal settings...
            </div>
          ) : (
            <form className="legal-settings-card" onSubmit={handleSubmit}>
              <div className="legal-settings-form-body">
                <div className="legal-settings-form-grid">
                  {/* NDA Requirement */}
                  <div className="legal-settings-form-group">
                    <label>NDA Requirement</label>
                    <select
                      name="nda_requirement"
                      value={formData.nda_requirement}
                      onChange={handleChange}
                      disabled={!isEditing}
                    >
                      {ndaRequirements.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Default Classification */}
                  <div className="legal-settings-form-group">
                    <label>Default Classification</label>
                    <select
                      name="default_classification"
                      value={formData.default_classification}
                      onChange={handleChange}
                      disabled={!isEditing}
                    >
                      {classifications.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Retention Policy */}
                  <div className="legal-settings-form-group legal-settings-form-group--full">
                    <label>Retention Policy</label>
                    <input
                      type="text"
                      name="retention_policy"
                      value={formData.retention_policy}
                      onChange={handleChange}
                      disabled={!isEditing}
                      placeholder="e.g. 7 years"
                      maxLength={100}
                    />
                  </div>
                </div>
              </div>

              <div className="legal-settings-form-actions">
                {!isEditing ? (
                  <button
                    type="button"
                    className="legal-settings-save-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="legal-settings-cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="legal-settings-save-btn"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Legal Settings"}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddLegalSettings;